// frontend/src/components/DailyRequirementOverrideModal.js
// 特定日のパターン別必要人数オーバーライド設定モーダル
//
// 使い方:
//   通常は shift_patterns.required_by_day_of_week が適用されるが、
//   イベント等で特定日だけ別の人数構成にしたい場合に、このモーダルから上書きする。
//   オーバーライドは daily_required_overrides テーブルに保存され、
//   自動シフト作成および集計表示で優先される。

import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { supabase } from "../supabase";
import { X, Calendar, Save, Trash2, RotateCcw } from "lucide-react";

const WEEKDAY_KEYS = ["日", "月", "火", "水", "木", "金", "土"];

const DailyRequirementOverrideModal = ({
  isOpen,
  onClose,
  organizationId,
  shiftPatterns = [],
  holidays = [],
  initialDate = null,
}) => {
  const [selectedDate, setSelectedDate] = useState(
    initialDate || dayjs().format("YYYY-MM-DD")
  );
  // { [patternId]: { count: number | "", isOverridden: boolean } }
  const [counts, setCounts] = useState({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingId, setExistingId] = useState(null);
  // 既存オーバーライドリスト
  const [allOverrides, setAllOverrides] = useState([]);

  const weekday = useMemo(() => WEEKDAY_KEYS[dayjs(selectedDate).day()], [selectedDate]);
  const isHoliday = holidays?.includes(selectedDate);

  // パターンの曜日別デフォルト必要人数を取得
  const getDefaultRequired = (pattern) => {
    const map =
      pattern.requiredByDayOfWeek
      || pattern.required_by_day_of_week
      || pattern.requiredCount
      || pattern.required_count
      || pattern.configuration?.required_by_day_of_week
      || {};
    const base = Number(map[weekday] || 0);
    if (!isHoliday) return base;
    const adj = pattern.holidayAdjustment || pattern.configuration?.holiday_adjustment || "default";
    if (adj === "no_assign") return 0;
    if (adj === "reduce_count") return Math.max(0, Math.round(base * 0.7));
    if (adj === "increase_count") return Math.round(base * 1.3);
    if (adj === "custom_count") {
      return Number(
        pattern.holidayCustomCount
        ?? pattern.configuration?.holiday_custom_count
        ?? 0
      );
    }
    return base;
  };

  // 選択日に対するオーバーライドを読み込み
  const loadForDate = async (date) => {
    if (!organizationId || !date) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("daily_required_overrides")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("override_date", date)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;

      const overrideMap = data?.required_counts || {};
      const initial = {};
      shiftPatterns.forEach((p) => {
        const hit = overrideMap[p.id];
        initial[p.id] = {
          count: hit != null ? hit : getDefaultRequired(p),
          isOverridden: hit != null,
        };
      });
      setCounts(initial);
      setNote(data?.note || "");
      setExistingId(data?.id || null);
    } catch (e) {
      console.error("オーバーライド読込エラー:", e);
      setCounts({});
      setNote("");
      setExistingId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadAllOverrides = async () => {
    if (!organizationId) return;
    try {
      const { data, error } = await supabase
        .from("daily_required_overrides")
        .select("override_date, required_counts, note")
        .eq("organization_id", organizationId)
        .order("override_date", { ascending: true });
      if (error) throw error;
      setAllOverrides(data || []);
    } catch (e) {
      console.error("オーバーライド一覧取得エラー:", e);
      setAllOverrides([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadForDate(selectedDate);
      loadAllOverrides();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDate, organizationId]);

  const handleCountChange = (patternId, value) => {
    setCounts((prev) => ({
      ...prev,
      [patternId]: {
        count: value === "" ? "" : Number(value),
        isOverridden: true,
      },
    }));
  };

  const handleResetPattern = (patternId, pattern) => {
    setCounts((prev) => ({
      ...prev,
      [patternId]: {
        count: getDefaultRequired(pattern),
        isOverridden: false,
      },
    }));
  };

  const handleSave = async () => {
    if (!organizationId || !selectedDate) return;
    setSaving(true);
    try {
      // 「オーバーライド指定された分のみ」を required_counts に保存
      const required = {};
      Object.entries(counts).forEach(([pid, v]) => {
        if (v.isOverridden && v.count !== "" && v.count != null) {
          required[pid] = Number(v.count);
        }
      });
      const payload = {
        organization_id: organizationId,
        override_date: selectedDate,
        required_counts: required,
        note: note || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from("daily_required_overrides")
        .upsert(payload, { onConflict: "organization_id,override_date" });
      if (error) throw error;
      await loadAllOverrides();
      await loadForDate(selectedDate);
      alert(`✅ ${selectedDate} のオーバーライドを保存しました`);
    } catch (e) {
      console.error("保存エラー:", e);
      alert("保存に失敗しました: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingId) return;
    if (!window.confirm(`${selectedDate} のオーバーライドを削除しますか？`)) return;
    try {
      const { error } = await supabase
        .from("daily_required_overrides")
        .delete()
        .eq("id", existingId)
        .eq("organization_id", organizationId);
      if (error) throw error;
      setExistingId(null);
      setNote("");
      await loadForDate(selectedDate);
      await loadAllOverrides();
      alert(`✅ ${selectedDate} のオーバーライドを削除しました`);
    } catch (e) {
      console.error("削除エラー:", e);
      alert("削除に失敗しました: " + e.message);
    }
  };

  if (!isOpen) return null;

  const sortedPatterns = [...shiftPatterns]
    .filter((p) => !p.isChainOnlyPattern && (p.displayMark || p.short_name))
    .sort((a, b) => (a.priority || 99) - (b.priority || 99));

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Calendar size={22} color="#0891b2" />
            <h2 style={styles.title}>特定日の必要人数オーバーライド</h2>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.body}>
          {/* 日付選択 + メタ情報 */}
          <div style={styles.section}>
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div>
                <label style={styles.label}>対象日</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
              <div style={{ fontSize: "0.9rem", color: "#475569" }}>
                <div>曜日: <strong>{weekday}</strong></div>
                {isHoliday && (
                  <div style={{ color: "#b91c1c", fontWeight: 600 }}>
                    🎌 祝日設定あり
                  </div>
                )}
                {existingId && (
                  <div style={{ color: "#0891b2", fontWeight: 600 }}>
                    ✏️ 既にオーバーライド登録あり
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: "0.75rem" }}>
              <label style={styles.label}>メモ（任意）</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="例: 院内研修のため通常より少なめ"
                style={styles.textInput}
              />
            </div>
          </div>

          {/* パターンごとの設定 */}
          <div style={styles.section}>
            <div style={styles.infoBox}>
              <p style={{ margin: 0, fontSize: "0.85rem", color: "#0c4a6e", lineHeight: 1.5 }}>
                各パターンの必要人数を上書きできます。<br />
                <strong>「リセット」</strong> を押すと、その日の通常値（曜日設定）に戻ります。<br />
                <strong>未編集（リセット状態）のパターンは保存されず</strong>、通常通り曜日設定が適用されます。
              </p>
            </div>
            {loading ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
                読み込み中...
              </div>
            ) : sortedPatterns.length === 0 ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "#64748b" }}>
                勤務パターンが登録されていません
              </div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.tableHeaderCell}>パターン</th>
                    <th style={styles.tableHeaderCell}>通常値<br />（{weekday}曜）</th>
                    <th style={styles.tableHeaderCell}>この日の必要人数</th>
                    <th style={styles.tableHeaderCell}>状態</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPatterns.map((p) => {
                    const c = counts[p.id] || { count: getDefaultRequired(p), isOverridden: false };
                    const defaultV = getDefaultRequired(p);
                    return (
                      <tr key={p.id} style={styles.tableRow}>
                        <td style={styles.tableCell}>
                          <div style={{ fontWeight: 600 }}>
                            <span style={styles.markBadge}>{p.displayMark || p.short_name || "?"}</span>
                            {p.name}
                          </div>
                        </td>
                        <td style={styles.tableCellCenter}>
                          <span style={{ color: "#64748b" }}>{defaultV} 人</span>
                        </td>
                        <td style={styles.tableCellCenter}>
                          <input
                            type="number"
                            value={c.count}
                            onChange={(e) => handleCountChange(p.id, e.target.value)}
                            min="0"
                            max="50"
                            style={{
                              ...styles.numberInput,
                              borderColor: c.isOverridden ? "#0ea5e9" : "#d1d5db",
                              background: c.isOverridden ? "#f0f9ff" : "white",
                            }}
                          />
                          <span style={{ marginLeft: "0.3rem", color: "#64748b", fontSize: "0.85rem" }}>人</span>
                          {c.isOverridden && (
                            <button
                              onClick={() => handleResetPattern(p.id, p)}
                              style={styles.resetBtn}
                              title="通常値に戻す"
                            >
                              <RotateCcw size={12} />
                            </button>
                          )}
                        </td>
                        <td style={styles.tableCellCenter}>
                          {c.isOverridden ? (
                            <span style={{ color: "#0891b2", fontSize: "0.8rem", fontWeight: 600 }}>
                              上書き中
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                              通常値
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 既存オーバーライド一覧 */}
          {allOverrides.length > 0 && (
            <div style={styles.section}>
              <h3 style={{ ...styles.label, marginBottom: "0.5rem" }}>
                既存のオーバーライド ({allOverrides.length}件)
              </h3>
              <div style={{ maxHeight: "150px", overflowY: "auto", fontSize: "0.85rem" }}>
                {allOverrides.map((o) => (
                  <div
                    key={o.override_date}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "0.4rem 0.6rem",
                      borderBottom: "1px solid #e2e8f0",
                      cursor: "pointer",
                      background: o.override_date === selectedDate ? "#e0f2fe" : "white",
                    }}
                    onClick={() => setSelectedDate(o.override_date)}
                  >
                    <span style={{ fontWeight: 600 }}>{o.override_date}</span>
                    <span style={{ color: "#64748b" }}>
                      {Object.keys(o.required_counts || {}).length} パターン {o.note ? `• ${o.note}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {existingId && (
            <button onClick={handleDelete} style={styles.dangerBtn}>
              <Trash2 size={14} /> この日の設定を削除
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={styles.secondaryBtn}>
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.primaryBtn,
              opacity: saving ? 0.6 : 1,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <Save size={14} /> {saving ? "保存中..." : "この日の設定を保存"}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: "20px",
  },
  modal: {
    background: "white",
    borderRadius: "12px",
    width: "min(800px, 100%)",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  header: {
    padding: "16px 20px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "#0f172a" },
  closeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#64748b",
    padding: "4px",
  },
  body: { padding: "16px 20px", overflowY: "auto", flex: 1 },
  footer: {
    padding: "12px 20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  section: { marginBottom: "1.25rem" },
  label: {
    display: "block",
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#475569",
    marginBottom: "0.35rem",
  },
  dateInput: {
    padding: "0.5rem 0.75rem",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "0.95rem",
  },
  textInput: {
    width: "100%",
    padding: "0.5rem 0.75rem",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "0.95rem",
    boxSizing: "border-box",
  },
  infoBox: {
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    marginBottom: "0.75rem",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" },
  tableHeaderRow: { background: "#f8fafc" },
  tableHeaderCell: {
    padding: "0.5rem",
    textAlign: "center",
    fontWeight: 600,
    color: "#334155",
    fontSize: "0.85rem",
    borderBottom: "2px solid #cbd5e1",
  },
  tableRow: { borderBottom: "1px solid #f1f5f9" },
  tableCell: { padding: "0.5rem 0.75rem", verticalAlign: "middle" },
  tableCellCenter: { padding: "0.5rem", textAlign: "center", verticalAlign: "middle" },
  markBadge: {
    display: "inline-block",
    background: "#f1f5f9",
    color: "#475569",
    padding: "0.1rem 0.5rem",
    borderRadius: "4px",
    marginRight: "0.5rem",
    fontWeight: 700,
    fontSize: "0.85rem",
  },
  numberInput: {
    width: "60px",
    padding: "0.35rem 0.5rem",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "0.9rem",
    textAlign: "center",
  },
  resetBtn: {
    marginLeft: "0.4rem",
    background: "transparent",
    border: "1px solid #cbd5e1",
    borderRadius: "4px",
    padding: "0.2rem 0.35rem",
    cursor: "pointer",
    color: "#64748b",
    fontSize: "0.7rem",
  },
  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "0.55rem 1.1rem",
    background: "linear-gradient(135deg, #0891b2, #0e7490)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  secondaryBtn: {
    padding: "0.55rem 1rem",
    background: "white",
    border: "1px solid #cbd5e1",
    color: "#475569",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.9rem",
  },
  dangerBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "0.5rem 0.9rem",
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#991b1b",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 600,
  },
};

export default DailyRequirementOverrideModal;
